  import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
  import AuthInterceptor from "./AuthInterceptor";
  import { Base_URL } from "../Client/apiURL";
  import toast from "react-hot-toast";
  import {
    mergeCurrentUserData,
    readStoredUserData,
    writeStoredUserData,
  } from "./userSession";

  const AuthContext = createContext({});

  export const AuthProvider = ({ children }) => {
    const initialUserRef = useRef(readStoredUserData());
    const [isAuthenticated, setIsAuthenticated] = useState(Boolean(initialUserRef.current));
    const [user, setUser] = useState(initialUserRef.current);
    const [isUserHydrated, setIsUserHydrated] = useState(!initialUserRef.current);
    const [sessionExpired, setSessionExpired] = useState(false);
    const refreshPromiseRef = useRef(null);
    const sessionGenerationRef = useRef(0);
    
    const logout = useCallback(async (options = {}) => {
      sessionGenerationRef.current += 1;
      try {
        const response = await fetch(`${Base_URL}/app/users/logout`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
  
        if (!response.ok) {
          console.error("Logout API call failed", response.statusText);
        }
      } catch (error) {
        console.error("Error during logout API call:", error);
      } finally {
        writeStoredUserData(null);
        localStorage.removeItem("superAdminAnnouncementDismissed");
        localStorage.removeItem("announcementDismissed");
        localStorage.removeItem("superAdminThumbnailDismissed");
        localStorage.removeItem("thumbnailDismissed");
        sessionStorage.removeItem("storedQuestion");
        sessionStorage.removeItem("SearchedQuestion_QuestionId");
        setIsAuthenticated(false);
        setUser(null);
        setSessionExpired(options.reason === "session-expired");
      }
    }, []);

    const applyUserData = useCallback((nextUser) => {
      const previousUser = readStoredUserData();
      const mergedUser = mergeCurrentUserData(previousUser, nextUser);
      writeStoredUserData(mergedUser);
      setUser(mergedUser);
      setIsAuthenticated(true);
      if (!previousUser?.IsSubscribed && mergedUser?.IsSubscribed) {
        toast.success("Your subscription is now active.");
      }
      return mergedUser;
    }, []);

    const refreshUser = useCallback(async () => {
      if (!readStoredUserData()) {
        setIsUserHydrated(true);
        return null;
      }
      if (refreshPromiseRef.current) return refreshPromiseRef.current;

      const generation = sessionGenerationRef.current;
      const request = (async () => {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch(`${Base_URL}/app/users/session-user`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: controller.signal,
          });
          const result = await response.json();
          if (
            generation === sessionGenerationRef.current
            && response.ok
            && result?.responseCode === 200
            && result?.response
          ) {
            return applyUserData(result.response);
          }
          return null;
        } catch (error) {
          if (error?.name !== "AbortError") {
            console.warn("Unable to refresh current user; keeping cached session data.", error);
          }
          return null;
        } finally {
          window.clearTimeout(timeoutId);
          if (generation === sessionGenerationRef.current) setIsUserHydrated(true);
          refreshPromiseRef.current = null;
        }
      })();

      refreshPromiseRef.current = request;
      return request;
    }, [applyUserData]);

    useEffect(() => {
      AuthInterceptor(logout);
      refreshUser();

      const handleFocus = () => refreshUser();
      const handleVisibility = () => {
        if (document.visibilityState === "visible") refreshUser();
      };
      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibility);
      return () => {
        window.removeEventListener("focus", handleFocus);
        document.removeEventListener("visibilitychange", handleVisibility);
      };
    }, [logout, refreshUser]);

    const login = (userData) => {
      sessionGenerationRef.current += 1;
      writeStoredUserData(userData);
      localStorage.setItem("pte-type", "pte academic");
      setIsAuthenticated(true);
      setUser(userData);
      setSessionExpired(false);
      setIsUserHydrated(true);
    };

    return (
      <AuthContext.Provider value={{ isAuthenticated, user, isUserHydrated, login, logout, refreshUser, applyUserData, sessionExpired, setSessionExpired }}>
        {children}
      </AuthContext.Provider>
    );
  };

  export const useAuth = () => useContext(AuthContext);
